/**
 * TerminalSection — "say something." 
 *
 * Phases, prompt format and boot sequence are carried over from v2:
 *   username -> booting (5 lines) -> ready (`sudo help` pre-filled)
 *
 * What changed in v3.2
 * --------------------
 * 1. `say` now really transmits. v2 rendered the confirmation locally without
 *    calling anything; v3 routes through POST /api/say into the Telegram group.
 * 2. Replies to your message come back. CommsContext polls
 *    GET /api/inbox/{sessionId}; each reply is appended to the transcript as a
 *    human-readable `[incoming transmission]` entry — never a raw message id.
 * 3. The transcript is a real scroll region (`dmr-scroll`): overscroll is
 *    contained and touch-action is pan-y, so scrolling to the end of the log no
 *    longer drags or locks the page (v2 bug).
 * 4. Command handling lives in lib/commands.js so the fixed uplink bar at the
 *    bottom of the viewport accepts the same commands with the same output.
 * 5. Optimistic rendering: Enter echoes the command at once and shows a
 *    `transmitting…` pending line, which the real output replaces on resolve.
 * 6. The username is global (CommsContext), so a name typed in the bottom bar
 *    boots this terminal, and a name typed here arms the bar.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useComms } from '@/lib/CommsContext';
import { useRadio } from '@/lib/RadioContext';
import { executeCommand, formatIncomingReply } from '@/lib/commands';
import { BOOT_MESSAGES, WHOAMI_FALLBACK_RESPONSES } from '@/lib/verses';
import { fetchWhoamiResponses } from '@/lib/api';

const MAX_ENTRIES = 200;

// Placeholder shown while a command is in flight. Kept lo-fi and lowercase so
// it reads as part of the transcript rather than a UI widget.
const PENDING_LABELS = {
  say: 'transmitting',
  moony: 'waking moony',
  name: 'rewriting the signature',
  relay: 'listening to the uplink',
  now: 'tuning in',
  whoami: 'looking',
  time: 'checking the clock',
  help: 'opening the unseen',
  sudo: 'opening the unseen',
};

/** Render one transcript entry. */
function Entry({ entry, prompt }) {
  switch (entry.type) {
    case 'boot':
      return <p className="dmr-line dmr-line--dim">{entry.content}</p>;
    case 'input':
      return (
        <p className="dmr-line dmr-line--body">
          <span className="text-[color:var(--phosphor)]">{entry.content}</span>
        </p>
      );
    case 'command':
      return (
        <p className="dmr-line dmr-line--body">
          <span className="text-[color:var(--phosphor)]">{prompt}</span>
          {entry.content}
        </p>
      );
    case 'verse':
      return <pre className="dmr-line dmr-verse">{entry.content}</pre>;
    case 'pending':
      return (
        <p className="dmr-line dmr-pending" data-testid="terminal-pending" aria-live="polite">
          {entry.content}
          <span className="dmr-pending__dots" aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
          <span className="dmr-caret" aria-hidden="true">
            ▋
          </span>
        </p>
      );
    case 'reply':
      return (
        <div className="dmr-reply">
          <p className="dmr-line dmr-line--body">{entry.content}</p>
          {entry.meta && <p className="dmr-hint mt-1">{entry.meta}</p>}
        </div>
      );
    case 'error':
      return <p className="dmr-line dmr-line--error">{entry.content}</p>;
    default:
      // output | help | say
      return <pre className="dmr-line dmr-line--output">{entry.content}</pre>;
  }
}

export default function TerminalSection({ onOpenMoony }) {
  const {
    startSession,
    recordCommand,
    say,
    subscribe,
    username: sessionUsername,
    relayStatus,
    registerTerminal,
  } = useComms();
  const radio = useRadio();

  const [phase, setPhase] = useState('username'); // username | booting | ready
  const [bootIndex, setBootIndex] = useState(0);
  const [entries, setEntries] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [whoamiResponses, setWhoamiResponses] = useState(WHOAMI_FALLBACK_RESPONSES);

  const inputRef = useRef(null);
  const terminalRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const append = useCallback((items) => {
    setEntries((prev) => {
      const next = [...prev, ...items];
      return next.length > MAX_ENTRIES ? next.slice(-MAX_ENTRIES) : next;
    });
  }, []);

  // ------------------------------------------------- optimistic rendering
  // `beginPending` echoes the command immediately and returns a token;
  // `resolvePending` swaps the animated placeholder for the real output in
  // place, so lines that arrived while waiting keep their order.
  const beginPending = useCallback(
    (commandLine) => {
      const token = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const verb = String(commandLine).trim().toLowerCase().split(/\s+/)[0];
      const placeholder = PENDING_LABELS[verb] || 'processing';
      append([
        { type: 'command', content: commandLine },
        { type: 'pending', content: placeholder, token },
      ]);
      return token;
    },
    [append],
  );

  const resolvePending = useCallback((token, items) => {
    setEntries((prev) => {
      const index = prev.findIndex((entry) => entry.token === token);
      if (index === -1) {
        const merged = [...prev, ...items];
        return merged.length > MAX_ENTRIES ? merged.slice(-MAX_ENTRIES) : merged;
      }
      const next = [...prev.slice(0, index), ...items, ...prev.slice(index + 1)];
      return next.length > MAX_ENTRIES ? next.slice(-MAX_ENTRIES) : next;
    });
  }, []);

  const clearTranscript = useCallback(() => setEntries([]), []);

  // Expose the transcript to the fixed uplink bar so anything typed there is
  // mirrored here as if it had been typed in this window.
  useEffect(
    () =>
      registerTerminal({
        append,
        beginPending,
        resolvePending,
        clear: clearTranscript,
      }),
    [registerTerminal, append, beginPending, resolvePending, clearTranscript],
  );

  // ------------------------------------------------------------ whoami pool
  useEffect(() => {
    let cancelled = false;
    fetchWhoamiResponses(WHOAMI_FALLBACK_RESPONSES).then((responses) => {
      if (!cancelled && responses?.length) setWhoamiResponses(responses);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ------------------------------------------------------- inbound Telegram
  useEffect(() => subscribe((reply) => {
    append([formatIncomingReply(reply)]);
  }), [append, subscribe]);

  // ----------------------------------------------------------- boot sequence
  useEffect(() => {
    if (phase !== 'booting') return undefined;

    if (bootIndex < BOOT_MESSAGES.length) {
      const timer = setTimeout(() => {
        append([{ type: 'boot', content: BOOT_MESSAGES[bootIndex] }]);
        setBootIndex((index) => index + 1);
      }, 800);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setPhase('ready');
      setCurrentInput('sudo help');
    }, 500);
    return () => clearTimeout(timer);
  }, [phase, bootIndex, append]);

  // -------------------------------------------------------- auto-scroll
  useEffect(() => {
    const node = terminalRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [entries, currentInput, phase]);

  const prompt = sessionUsername ? `${sessionUsername}_saysomething@DeadMoon:~$ ` : '';

  const context = useMemo(
    () => ({
      username: sessionUsername,
      whoami: whoamiResponses,
      say,
      setName: startSession,
      radio,
      relayStatus,
      openMoony: onOpenMoony,
    }),
    [sessionUsername, whoamiResponses, say, startSession, radio, relayStatus, onOpenMoony],
  );

  // ---------------------------------------------------- global username sync
  // If the bottom bar (or `name <you>`) sets a username while this terminal is
  // still waiting, boot it so both inputs share one identity.
  useEffect(() => {
    if (sessionUsername && phase === 'username') {
      setCurrentInput('');
      setPhase('booting');
    }
  }, [sessionUsername, phase]);

  // -------------------------------------------------------- username phase
  const handleUsernameSubmit = async (event) => {
    event.preventDefault();
    const name = currentInput.trim();
    if (!name) return;

    append([{ type: 'input', content: `Enter username: ${name}` }]);
    setCurrentInput('');
    setPhase('booting');
    await startSession(name);
  };

  // --------------------------------------------------------- command phase
  const handleCommandSubmit = async (event) => {
    event.preventDefault();
    const line = currentInput.trim();
    if (!line) return;

    setCurrentInput('');
    setHistoryIndex(-1);
    recordCommand(line);

    // Optimistic: the line and a placeholder render before any await.
    const token = beginPending(line);
    const results = await executeCommand(line, context);
    if (!mounted.current) return;

    if (results.some((result) => result.type === 'clear')) {
      clearTranscript();
      return;
    }
    resolvePending(
      token,
      results.filter((result) => result.type !== 'command'),
    );
  };

  // v2 arrow-key command history, preserved.
  const handleKeyDown = (event) => {
    const commands = entries.filter((entry) => entry.type === 'command').map((entry) => entry.content);

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (historyIndex < commands.length - 1) {
        const nextIndex = historyIndex + 1;
        setHistoryIndex(nextIndex);
        setCurrentInput(commands[commands.length - 1 - nextIndex] || '');
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (historyIndex > 0) {
        const nextIndex = historyIndex - 1;
        setHistoryIndex(nextIndex);
        setCurrentInput(commands[commands.length - 1 - nextIndex] || '');
      } else {
        setHistoryIndex(-1);
        setCurrentInput('');
      }
    }
  };

  const focusInput = (event) => {
    event.stopPropagation();
    inputRef.current?.focus();
  };

  return (
    <section
      id="terminal-section"
      className="dmr-section bg-[#050505]"
      aria-label="Communications terminal"
    >
      <div className="w-full max-w-4xl">
        <motion.div
          className="mb-8 text-center"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="dmr-heading mb-2 text-2xl md:text-4xl">say something.</h2>
          <p className="text-sm text-[color:var(--phosphor-60)]">
            someone will hear it — and may answer from the group
          </p>
        </motion.div>

        <motion.div
          className="dmr-panel cursor-text overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          onClick={focusInput}
        >
          <div className="dmr-panel__bar">
            <span className="dmr-dot dmr-dot--close" />
            <span className="dmr-dot dmr-dot--min" />
            <span className="dmr-dot dmr-dot--max" />
            <span className="dmr-panel__title">deadmoon@terminal</span>
            <span
              className={`dmr-led ml-auto ${
                relayStatus.enabled ? (relayStatus.pollerRunning ? '' : 'dmr-led--busy') : 'dmr-led--idle'
              }`}
              title={
                relayStatus.enabled
                  ? 'telegram uplink live'
                  : 'telegram uplink not configured'
              }
              aria-label={relayStatus.enabled ? 'Telegram uplink live' : 'Telegram uplink offline'}
            />
          </div>

          {/* Transcript: a contained inner scroller. */}
          <div
            ref={terminalRef}
            className="dmr-scroll dmr-scroll--faded h-[45vh] min-h-[18rem] p-4 md:h-[52vh]"
            data-testid="terminal-transcript"
          >
            {entries.map((entry, index) => (
              <div key={index} className="mb-1">
                <Entry entry={entry} prompt={prompt} />
              </div>
            ))}

            {phase === 'username' && (
              <form onSubmit={handleUsernameSubmit} className="flex items-center">
                <span className="shrink-0 text-[color:var(--phosphor)]">Enter username: </span>
                <input
                  ref={inputRef}
                  className="dmr-input"
                  type="text"
                  value={currentInput}
                  maxLength={50}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setCurrentInput(event.target.value)}
                  data-testid="terminal-input"
                  aria-label="Enter username"
                />
                <span className="dmr-caret shrink-0" aria-hidden="true">
                  ▋
                </span>
                <button
                  type="submit"
                  className="dmr-button shrink-0 !px-2 !py-1"
                  data-testid="terminal-submit"
                  aria-label="Enter the terminal"
                >
                  ⏎
                </button>
              </form>
            )}

            {phase === 'booting' && <p className="dmr-line dmr-line--dim">...</p>}

            {phase === 'ready' && (
              <form onSubmit={handleCommandSubmit} className="flex items-center">
                <span className="shrink-0 text-[color:var(--phosphor)]">{prompt}</span>
                <input
                  ref={inputRef}
                  className="dmr-input"
                  type="text"
                  value={currentInput}
                  maxLength={500}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(event) => setCurrentInput(event.target.value)}
                  onKeyDown={handleKeyDown}
                  data-testid="terminal-input"
                  aria-label="Terminal command"
                />
                <span className="dmr-caret shrink-0" aria-hidden="true">
                  ▋
                </span>
                <button
                  type="submit"
                  className="dmr-button shrink-0 !px-2 !py-1"
                  data-testid="terminal-submit"
                  aria-label="Run command"
                >
                  ⏎
                </button>
              </form>
            )}
          </div>
        </motion.div>

        <motion.p
          className="dmr-hint mt-4 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          There is at least one person in the void listening.
        </motion.p>
      </div>
    </section>
  );
}
